import { useState, useRef, useEffect } from 'react';
import { Brain, Sparkles, RefreshCw, AlertCircle, Send, Download, Copy, Check, Plus, MessageSquare, Trash2, Edit2 } from '../icons';
import { MarkdownRenderer } from './MarkdownRenderer';
import { QUICK_TOPICS, FULL_REPORT_PROMPT } from './constants';
import { buildAIPrompt } from './buildAIPrompt';
import { supabase } from '../../lib/supabase';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ConversationSummary {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

interface AIAnalysisPanelProps {
  analysis: string | undefined;
  scores: Score[];
  harmonicScores: HarmonicScore[];
  resultId: string;
  showRegenerate?: boolean;
  respondentName?: string;
  storageField?: 'ai_analysis' | 'coach_ai_analysis';
  onAnalysisUpdate: (analysis: string) => void;
}

const CONTINUATION_MARKER = 'Continue generating Part';
const MAX_CONTINUATIONS = 4;

export function AIAnalysisPanel({
  analysis,
  scores,
  harmonicScores,
  resultId,
  showRegenerate = false,
  respondentName,
  storageField = 'ai_analysis',
  onAnalysisUpdate,
}: AIAnalysisPanelProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [copied, setCopied] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isSavingEnabled, setIsSavingEnabled] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversationTitle, setConversationTitle] = useState<string>('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const analysisRef = useRef<HTMLDivElement>(null);
  const loadedResultIdRef = useRef<string | null>(null);

  // Load conversation list on mount / when resultId changes
  useEffect(() => {
    if (loadedResultIdRef.current === resultId) return;
    loadedResultIdRef.current = resultId;
    setMessages([]);
    setConversationId(null);
    setConversationTitle('');
    setIsEditingTitle(false);
    loadConversationList();
  }, [resultId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversationList = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: convs } = await supabase
        .from('ai_analysis_conversations')
        .select('id, title, created_at, updated_at')
        .eq('result_id', resultId)
        .eq('user_id', user.id)
        .eq('analysis_type', 'standard')
        .order('updated_at', { ascending: false });

      if (!convs) {
        setConversations([]);
        return;
      }

      const summaries: ConversationSummary[] = [];
      for (const conv of convs) {
        const { count } = await supabase
          .from('ai_analysis_messages')
          .select('id', { count: 'exact', head: true })
          .eq('conversation_id', conv.id);

        let title = conv.title;
        if (!title) {
          const { data: firstMsg } = await supabase
            .from('ai_analysis_messages')
            .select('content')
            .eq('conversation_id', conv.id)
            .eq('role', 'user')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          title = firstMsg?.content
            ? firstMsg.content.length > 60
              ? firstMsg.content.slice(0, 60) + '...'
              : firstMsg.content
            : 'Conversation';
        }

        summaries.push({
          id: conv.id,
          title,
          created_at: conv.created_at,
          updated_at: conv.updated_at,
          message_count: count || 0,
        });
      }
      setConversations(summaries);
    } catch (err) {
      console.error('Error loading conversation list:', err);
      setConversations([]);
    }
  };

  const loadConversation = async (convId: string) => {
    setHistoryLoading(true);
    try {
      const { data: conv } = await supabase
        .from('ai_analysis_conversations')
        .select('title')
        .eq('id', convId)
        .maybeSingle();

      const { data: dbMessages } = await supabase
        .from('ai_analysis_messages')
        .select('id, role, content, created_at')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });

      if (dbMessages && dbMessages.length > 0) {
        setConversationId(convId);
        setMessages(dbMessages.map(m => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date(m.created_at),
        })));
      }

      const convSummary = conversations.find(c => c.id === convId);
      setConversationTitle(conv?.title || convSummary?.title || '');
      setShowHistory(false);
    } catch (err) {
      console.error('Error loading conversation:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const renameConversation = async (convId: string, newTitle: string) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    setIsSavingTitle(true);
    try {
      const { error } = await supabase
        .from('ai_analysis_conversations')
        .update({ title: trimmed })
        .eq('id', convId);

      if (error) {
        console.error('Failed to rename conversation:', error.message);
        return;
      }

      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, title: trimmed } : c
      ));
      setConversationTitle(trimmed);
    } catch (err) {
      console.error('Error renaming conversation:', err);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const startEditingTitle = () => {
    setTitleDraft(conversationTitle);
    setIsEditingTitle(true);
  };

  const saveTitle = () => {
    if (!conversationId) return;
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== conversationTitle) {
      renameConversation(conversationId, trimmed);
    }
    setIsEditingTitle(false);
  };

  const cancelEditingTitle = () => {
    setIsEditingTitle(false);
    setTitleDraft('');
  };

  const deleteConversation = async (convId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await supabase
        .from('ai_analysis_messages')
        .delete()
        .eq('conversation_id', convId);

      await supabase
        .from('ai_analysis_conversations')
        .delete()
        .eq('id', convId);

      setConversations(prev => prev.filter(c => c.id !== convId));

      if (conversationId === convId) {
        setMessages([]);
        setConversationId(null);
        setConversationTitle('');
        setIsEditingTitle(false);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  const getUserId = async (): Promise<string | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  };

  const ensureConversation = async (userId: string): Promise<string | null> => {
    if (conversationId) return conversationId;

    const { data: created, error } = await supabase
      .from('ai_analysis_conversations')
      .insert({
        result_id: resultId,
        user_id: userId,
        role: 'coach',
        analysis_type: 'standard',
      })
      .select('id')
      .maybeSingle();

    if (error || !created) {
      console.error('Failed to create conversation:', error?.message);
      return null;
    }

    setConversationId(created.id);
    return created.id;
  };

  const saveMessage = async (
    convId: string,
    role: 'user' | 'assistant',
    content: string
  ): Promise<string | null> => {
    if (!isSavingEnabled) return null;

    const { data, error } = await supabase
      .from('ai_analysis_messages')
      .insert({
        conversation_id: convId,
        role,
        content,
      })
      .select('id')
      .maybeSingle();

    if (error) {
      console.error('Failed to save message:', error.message);
      setSaveWarning('This message may not have been saved permanently.');
      setTimeout(() => setSaveWarning(null), 5000);
      return null;
    }

    if (role === 'user' && !conversationTitle) {
      const autoTitle = content.length > 60 ? content.slice(0, 60) + '...' : content;
      await supabase
        .from('ai_analysis_conversations')
        .update({ updated_at: new Date().toISOString(), title: autoTitle })
        .eq('id', convId);
      setConversationTitle(autoTitle);
    } else {
      await supabase
        .from('ai_analysis_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', convId);
    }

    return data?.id || null;
  };

  const callGenerateAnalysis = async (
    prompt: string,
    conversationHistory?: { role: string; content: string }[],
    userId?: string | null
  ) => {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        prompt,
        conversation_history: conversationHistory,
        result_id: resultId,
        user_id: userId,
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to generate analysis');
    }

    return response.json();
  };

  const needsContinuation = (text: string): boolean => {
    const lastLines = text.slice(-200).toLowerCase();
    return lastLines.includes('continue generating part') ||
           lastLines.includes('continued in part') ||
           lastLines.includes('end of part');
  };

  const sendFullReport = async (instruction: string, isInitialGeneration: boolean) => {
    const prompt = buildAIPrompt(scores, harmonicScores, instruction);
    setIsGenerating(true);
    setAiError(null);
    setGenerationProgress('Generating report (Part 1)...');

    try {
      const userId = await getUserId();
      let fullContent = '';
      let continuationCount = 0;
      let conversationHistory: { role: string; content: string }[] = [];

      const firstData = await callGenerateAnalysis(prompt, undefined, userId);
      fullContent = firstData.analysis;
      conversationHistory = [
        { role: 'user', content: prompt },
        { role: 'assistant', content: fullContent },
      ];

      while (needsContinuation(fullContent) && continuationCount < MAX_CONTINUATIONS) {
        continuationCount++;
        setGenerationProgress(`Generating report (Part ${continuationCount + 1})...`);

        const continuationPrompt = buildAIPrompt(
          scores,
          harmonicScores,
          `Continue the report from where you left off. Continue writing the remaining sections. Do not repeat content already written. Maintain the same depth and quality. If there are more sections remaining after this, end with "Continue generating Part ${continuationCount + 2}." If this is the final part, write the conclusion and do not add a continuation marker.`
        );

        const contData = await callGenerateAnalysis(
          continuationPrompt,
          conversationHistory,
          userId
        );

        fullContent += '\n\n' + contData.analysis;
        conversationHistory.push(
          { role: 'user', content: continuationPrompt },
          { role: 'assistant', content: contData.analysis },
        );
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: fullContent,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (userId && isSavingEnabled) {
        const convId = await ensureConversation(userId);
        if (convId) {
          if (isInitialGeneration) {
            await saveMessage(convId, 'user', instruction);
          }
          const savedId = await saveMessage(convId, 'assistant', fullContent);
          if (savedId) {
            setMessages(prev => prev.map(m =>
              m.id === assistantMessage.id ? { ...m, id: savedId } : m
            ));
          }
          loadConversationList();
        }
      }

      if (isInitialGeneration) {
        const { error: updateError } = await supabase
          .from('results')
          .update({
            [storageField]: fullContent,
            [`${storageField}_generated_at`]: new Date().toISOString()
          })
          .eq('id', resultId);

        if (updateError) {
          console.error('Failed to save analysis:', updateError.message);
        }

        onAnalysisUpdate(fullContent);
      }
    } catch (err) {
      console.error('Error generating analysis:', err);
      setAiError((err as Error).message || 'Failed to generate AI analysis. Please try again.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  };

  const sendMessage = async (instruction: string, isInitialGeneration = false) => {
    const prompt = buildAIPrompt(scores, harmonicScores, instruction);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: instruction,
      timestamp: new Date(),
    };

    if (!isInitialGeneration) {
      setMessages(prev => [...prev, userMessage]);
    }

    setIsGenerating(true);
    setAiError(null);

    try {
      const userId = await getUserId();

      if (userId && !isInitialGeneration && isSavingEnabled) {
        const convId = await ensureConversation(userId);
        if (convId) {
          const savedId = await saveMessage(convId, 'user', instruction);
          if (savedId) {
            setMessages(prev => prev.map(m =>
              m.id === userMessage.id ? { ...m, id: savedId } : m
            ));
          }
        }
      }

      const conversationHistory = messages
        .map(m => ({ role: m.role, content: m.content }));

      const data = await callGenerateAnalysis(
        prompt,
        isInitialGeneration ? undefined : conversationHistory,
        userId
      );

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.analysis,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (userId && isSavingEnabled) {
        const convId = await ensureConversation(userId);
        if (convId) {
          if (isInitialGeneration) {
            await saveMessage(convId, 'user', instruction);
          }
          const savedId = await saveMessage(convId, 'assistant', data.analysis);
          if (savedId) {
            setMessages(prev => prev.map(m =>
              m.id === assistantMessage.id ? { ...m, id: savedId } : m
            ));
          }
          loadConversationList();
        }
      }

      if (isInitialGeneration) {
        const { error: updateError } = await supabase
          .from('results')
          .update({
            [storageField]: data.analysis,
            [`${storageField}_generated_at`]: new Date().toISOString()
          })
          .eq('id', resultId);

        if (updateError) {
          console.error('Failed to save analysis:', updateError.message);
        }

        onAnalysisUpdate(data.analysis);
      }
    } catch (err) {
      console.error('Error generating analysis:', err);
      setAiError((err as Error).message || 'Failed to generate AI analysis. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickTopic = (topic: typeof QUICK_TOPICS[0]) => {
    const isFullReport = 'isFullReport' in topic && topic.isFullReport;
    if (isFullReport) {
      sendFullReport(topic.prompt, messages.length === 0);
    } else {
      sendMessage(topic.prompt, messages.length === 0);
    }
  };

  const handleChatSubmit = () => {
    if (!chatInput.trim() || isGenerating) return;
    const instruction = chatInput.trim();
    setChatInput('');
    sendMessage(instruction, messages.length === 0);
  };

  const handleNewAnalysis = () => {
    setMessages([]);
    setConversationId(null);
    setConversationTitle('');
    setIsEditingTitle(false);
    sendFullReport(FULL_REPORT_PROMPT, true);
  };

  const handleCopy = async () => {
    const allAssistant = messages.filter(m => m.role === 'assistant').map(m => m.content).join('\n\n');
    if (!allAssistant) return;
    await navigator.clipboard.writeText(allAssistant);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = async () => {
    if (!analysisRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;

    const el = analysisRef.current;
    const parent = el.parentElement;
    const prevOverflow = el.style.overflow;
    const prevMaxHeight = el.style.maxHeight;
    const prevParentOverflow = parent?.style.overflow;

    el.style.overflow = 'visible';
    el.style.maxHeight = 'none';
    if (parent) parent.style.overflow = 'visible';

    try {
      await html2pdf()
        .set({
          margin: [15, 15, 15, 15],
          filename: `AI-Analysis${respondentName ? `-${respondentName}` : ''}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(el)
        .save();
    } finally {
      el.style.overflow = prevOverflow;
      el.style.maxHeight = prevMaxHeight;
      if (parent) parent.style.overflow = prevParentOverflow || '';
    }
  };

  const hasMessages = messages.length > 0;
  const hasAssistantMessage = messages.some(m => m.role === 'assistant');
  const savedConversationCount = conversations.length;

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        {/* Quick Topics Sidebar */}
        <div className="freud-card-elevated p-5 flex flex-col">
          <h3 className="text-sm font-bold text-chocolate-900 mb-3 uppercase tracking-wide">Quick Analysis</h3>
          <div className="flex flex-col gap-2">
            {QUICK_TOPICS.map((topic) => (
              <button
                key={topic.label}
                onClick={() => handleQuickTopic(topic)}
                disabled={isGenerating}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold text-left transition-all disabled:opacity-50 ${
                  'isFullReport' in topic && topic.isFullReport
                    ? 'bg-sage-600 text-white hover:bg-sage-700'
                    : 'bg-sage-50 text-sage-700 hover:bg-sage-100'
                }`}
              >
                {topic.label}
              </button>
            ))}
          </div>

          {savedConversationCount > 0 && (
            <button
              onClick={() => setShowHistory(true)}
              disabled={isGenerating}
              className="mt-4 px-4 py-2.5 rounded-xl text-sm font-semibold text-left bg-cream-50 text-chocolate-600 hover:bg-cream-100 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Saved Conversations ({savedConversationCount})
            </button>
          )}

          <div className="mt-4 pt-4 border-t border-cream-200">
            <button
              onClick={() => setIsSavingEnabled(prev => !prev)}
              className="w-full flex items-center justify-center gap-2 text-xs text-chocolate-400 hover:text-chocolate-600 transition-colors"
            >
              {isSavingEnabled ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {isSavingEnabled ? 'Auto-save on' : 'Auto-save off'}
            </button>
          </div>
        </div>

        {/* Conversation Area */}
        <div className="freud-card-elevated overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-cream-200">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <Brain className="w-5 h-5 text-sage-600 flex-shrink-0" />
              {isEditingTitle && conversationId ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTitle();
                      if (e.key === 'Escape') cancelEditingTitle();
                    }}
                    onBlur={saveTitle}
                    autoFocus
                    disabled={isSavingTitle}
                    className="flex-1 min-w-0 px-3 py-1 text-sm font-semibold border border-sage-400 rounded-lg focus:outline-none focus:border-sage-600 bg-white"
                    placeholder="Enter a name for this conversation..."
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="text-lg font-bold text-chocolate-900 truncate">
                    {conversationTitle || 'AI Analysis'}
                  </h2>
                  {conversationId && (
                    <button
                      onClick={startEditingTitle}
                      disabled={isGenerating}
                      className="p-1 text-chocolate-300 hover:text-sage-600 transition-colors flex-shrink-0"
                      title="Rename conversation"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isSavingEnabled && conversationId && (
                    <span className="text-xs text-sage-500 flex items-center gap-1 ml-1 flex-shrink-0">
                      <Check className="w-3 h-3" /> Saved
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {showRegenerate && (
                <button
                  onClick={handleNewAnalysis}
                  disabled={isGenerating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-all disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  New Analysis
                </button>
              )}
            </div>
          </div>

          {aiError && (
            <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{aiError}</p>
            </div>
          )}

          {saveWarning && (
            <div className="mx-6 mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{saveWarning}</p>
            </div>
          )}

          {showHistory && (
            <div className="mx-6 mt-4 border border-cream-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-cream-50 border-b border-cream-200">
                <h3 className="text-sm font-bold text-chocolate-900">Saved Conversations</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-chocolate-400 hover:text-chocolate-600 text-sm font-medium"
                >
                  Close
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-cream-100">
                {historyLoading ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-chocolate-400">Loading...</p>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-chocolate-400">No saved conversations yet.</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className="flex items-center justify-between px-4 py-3 hover:bg-cream-50 cursor-pointer transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-chocolate-800 truncate">{conv.title}</p>
                        <p className="text-xs text-chocolate-400 mt-0.5">
                          {new Date(conv.updated_at).toLocaleDateString()} at {new Date(conv.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            loadConversation(conv.id).then(() => {
                              setTitleDraft(conv.title);
                              setIsEditingTitle(true);
                            });
                          }}
                          className="p-2 text-chocolate-300 hover:text-sage-600 opacity-0 group-hover:opacity-100 transition-all"
                          title="Rename conversation"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => deleteConversation(conv.id, e)}
                          className="p-2 text-chocolate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                          title="Delete conversation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div ref={analysisRef} className="px-6 py-4 overflow-y-auto max-h-[80vh] flex-1 space-y-4">
            {!hasMessages && !isGenerating ? (
              <div className="py-16 text-center">
                <Sparkles className="w-12 h-12 text-cream-400 mx-auto mb-4" />
                <p className="text-chocolate-500 mb-2">NLP OS™ AI Analysis</p>
                <p className="text-sm text-chocolate-400 mb-6 max-w-md mx-auto">
                  Generate a full report or select a quick analysis topic. Ask follow-up questions to go deeper into the results. All questions and answers are saved automatically.
                </p>
                <button
                  onClick={() => sendFullReport(FULL_REPORT_PROMPT, true)}
                  className="freud-btn-primary inline-flex items-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Generate Full Report
                </button>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] rounded-2xl px-5 py-3 ${
                      msg.role === 'user'
                        ? 'bg-sage-600 text-white'
                        : 'bg-cream-50 border border-cream-200'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <MarkdownRenderer content={msg.content} />
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {isGenerating && (
                  <div className="flex justify-start">
                    <div className="bg-cream-50 border border-cream-200 rounded-2xl px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-sage-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        {generationProgress && (
                          <span className="text-sm text-chocolate-500">{generationProgress}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Chat Input */}
          <div className="px-6 py-4 border-t border-cream-200">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder={
                  hasMessages
                    ? (respondentName ? `Ask more about ${respondentName}'s results...` : 'Ask a follow-up question...')
                    : 'Ask about the assessment results...'
                }
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
                disabled={isGenerating}
                className="freud-input"
              />
              <button
                onClick={handleChatSubmit}
                disabled={isGenerating || !chatInput.trim()}
                className="p-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50 flex-shrink-0"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Export Options */}
      {hasAssistantMessage && (
        <div className="freud-card-elevated p-6">
          <h3 className="text-lg font-bold text-chocolate-900 mb-4">Export Options</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={handleExportPDF}
              disabled={isGenerating}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-chocolate-700 text-white rounded-xl font-semibold hover:bg-chocolate-800 transition-all disabled:opacity-40"
            >
              <Download className="w-5 h-5" />
              Export as PDF
            </button>
            <button
              onClick={handleCopy}
              disabled={isGenerating}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-chocolate-500 text-white rounded-xl font-semibold hover:bg-chocolate-600 transition-all disabled:opacity-40"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? 'Copied!' : 'Copy All Responses'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
